import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { experiences } from "../../data/constanit";
import { Briefcase, Calendar, MapPin, CheckCircle2 } from "lucide-react";

const Container = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  padding: 80px 24px 60px;

  @media (max-width: 768px) {
    padding: 60px 16px 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled(motion.h2)`
  font-size: 42px;
  font-weight: 800;
  text-align: center;
  color: ${({ theme }) => theme.text_primary};
  margin-bottom: 12px;
  letter-spacing: -0.5px;

  span {
    background: linear-gradient(135deg, ${({ theme }) => theme.primary} 0%, ${({ theme }) => theme.secondary} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Description = styled(motion.p)`
  font-size: 16px;
  text-align: center;
  max-width: 620px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.7;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    font-size: 14.5px;
    margin-bottom: 24px;
  }
`;

const TimelineContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 28px;

  &::before {
    content: "";
    position: absolute;
    top: 20px;
    bottom: 20px;
    left: 28px;
    width: 2px;
    background: linear-gradient(
      180deg,
      ${({ theme }) => theme.primary} 0%,
      ${({ theme }) => theme.secondary} 60%,
      transparent 100%
    );

    @media (max-width: 640px) {
      left: 20px;
    }
  }
`;

const TimelineItem = styled(motion.div)`
  position: relative;
  display: flex;
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 640px) {
    gap: 14px;
  }
`;

const TimelineNode = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${({ theme }) => theme.card_light};
  border: 1.5px solid ${({ theme }) => theme.primary};
  box-shadow: 0 0 16px ${({ theme }) => `${theme.primary}33`};
  display: grid;
  place-items: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.primary};
  z-index: 2;

  @media (max-width: 640px) {
    width: 40px;
    height: 40px;
    border-radius: 12px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const ExperienceCard = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.card_light};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 20px;
  padding: 24px 28px;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.25);
  transition: all 0.25s ease;

  &:hover {
    border-color: ${({ theme }) => `${theme.primary}66`};
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
    transform: translateY(-2px);
  }

  @media (max-width: 640px) {
    padding: 18px 18px;
    border-radius: 16px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const Role = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  line-height: 1.3;
`;

const CompanyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.primary};
  margin-top: 4px;
  flex-wrap: wrap;
`;

const LocationText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.text_secondary};
  font-weight: 400;
  font-size: 13px;
`;

const DateBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 999px;
  background: ${({ theme }) => theme.bgSoft};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text_secondary};
`;

const ProjectBlock = styled.div`
  margin-top: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  background: ${({ theme }) => `${theme.bgSoft}80`};
  border: 1px solid ${({ theme }) => theme.border};
`;

const ProjectTitle = styled.h4`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  margin-bottom: 8px;
`;

const BulletsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const BulletItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13.5px;
  line-height: 1.6;
  color: ${({ theme }) => theme.text_secondary};

  svg {
    color: ${({ theme }) => theme.primary};
    flex-shrink: 0;
    margin-top: 3px;
  }
`;

const SkillsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.border};
`;

const SkillTag = styled.span`
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ theme }) => `${theme.primary}12`};
  color: ${({ theme }) => theme.primary};
  border: 1px solid ${({ theme }) => `${theme.primary}2f`};
`;

const Experience = () => {
  return (
    <Container id="experience">
      <ContentWrapper>
        <Title
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Work <span>Experience</span>
        </Title>

        <Description
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          My professional software engineering experience across companies and full-stack production systems.
        </Description>

        <TimelineContainer>
          {experiences.map((exp, index) => (
            <TimelineItem
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <TimelineNode>
                <Briefcase size={20} />
              </TimelineNode>

              <ExperienceCard>
                <CardHeader>
                  <div>
                    <Role>{exp.role}</Role>
                    <CompanyRow>
                      <span>{exp.company}</span>
                      <span>•</span>
                      <LocationText>
                        <MapPin size={12} />
                        {exp.location}
                      </LocationText>
                    </CompanyRow>
                  </div>
                  <DateBadge>
                    <Calendar size={12} />
                    {exp.date}
                  </DateBadge>
                </CardHeader>

                {/* Sub-projects breakdown for Krishaa Soft */}
                {exp.projects &&
                  exp.projects.map((proj, pIdx) => (
                    <ProjectBlock key={pIdx}>
                      <ProjectTitle>{proj.title}</ProjectTitle>
                      <BulletsList>
                        {proj.bullets.map((bullet, bIdx) => (
                          <BulletItem key={bIdx}>
                            <CheckCircle2 size={14} />
                            <span>{bullet}</span>
                          </BulletItem>
                        ))}
                      </BulletsList>
                    </ProjectBlock>
                  ))}

                {/* Direct bullets for WenBear and Precious */}
                {exp.bullets && (
                  <BulletsList style={{ marginTop: "12px" }}>
                    {exp.bullets.map((bullet, bIdx) => (
                      <BulletItem key={bIdx}>
                        <CheckCircle2 size={14} />
                        <span>{bullet}</span>
                      </BulletItem>
                    ))}
                  </BulletsList>
                )}

                {exp.skills && (
                  <SkillsWrapper>
                    {exp.skills.map((skill, idx) => (
                      <SkillTag key={idx}>{skill}</SkillTag>
                    ))}
                  </SkillsWrapper>
                )}
              </ExperienceCard>
            </TimelineItem>
          ))}
        </TimelineContainer>
      </ContentWrapper>
    </Container>
  );
};

export default Experience;
